import { useState } from "react";
import { Banknote, Download, Loader2, Printer, RotateCcw, Send, ThumbsDown, Stamp } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";

const today = () => isoDay(new Date());

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

/** Records what the authority did with a claim: filed, acknowledged, knocked back, repaid. */
export const ClaimActions = ({ claim, mayWrite, mayReopen, onDone, onDownload, onPrint }) => {
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [rejected, setRejected] = useState([]);

  const post = async (path, body, message) => {
    setBusy(true);
    try {
      await api.post(`/claims/${claim.claim_ref}/${path}`, body);
      toast.success(message);
      setOpen("");
      setForm({});
      setRejected([]);
      await onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy(false);
  };

  const rate = claim.reclaim > 0;
  const expected = claim.expected_repayment ?? claim.reclaim;
  const live = claim.rows.filter((r) => !r.rejected);

  const step = () => {
    if (claim.status === "claimed") {
      return { key: "submit", label: "Mark as filed", icon: Send };
    }
    if (claim.status === "submitted") {
      return { key: "acknowledge", label: "Record acknowledgement", icon: Stamp };
    }
    if (["acknowledged", "part_repaid"].includes(claim.status) && rate) {
      return { key: "repayment", label: "Record repayment", icon: Banknote };
    }
    return null;
  };
  const next = step();

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {mayWrite && next && (
        <Dialog open={open === next.key} onOpenChange={(o) => {
          setOpen(o ? next.key : "");
          if (o) {
            setForm(next.key === "repayment"
              ? { received_on: today(), amount: expected, reference: "", explanation: "" }
              : next.key === "submit"
                ? { submitted_on: today(), authority_ref: "" }
                : { acknowledged_on: today(), acknowledgement_ref: "", note: "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid={`claim-${next.key}-${claim.claim_ref}`}
                    className="h-7 rounded-full bg-primary px-2.5 text-[10px] text-primary-foreground hover:bg-primary/90">
              <next.icon className="mr-1 h-3 w-3" /> {next.label}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {next.label} · {claim.claim_ref}
              </DialogTitle>
            </DialogHeader>

            {next.key === "submit" && (
              <div className="space-y-3">
                <Field label="Date filed">
                  <DateField value={form.submitted_on} testId="claim-submitted-on" clearable={false}
                             disabled={{ after: new Date() }}
                             onChange={(v) => setForm({ ...form, submitted_on: v })} />
                </Field>
                <Field label={`${claim.authority} reference`}
                       hint="HMRC correlation / submission ID, or the portal receipt number.">
                  <Input value={form.authority_ref || ""} data-testid="claim-authority-ref"
                         onChange={(e) => setForm({ ...form, authority_ref: e.target.value })}
                         className="bg-muted/40 font-mono text-[12px]" />
                </Field>
              </div>
            )}

            {next.key === "acknowledge" && (
              <div className="space-y-3">
                <Field label="Date acknowledged">
                  <DateField value={form.acknowledged_on} testId="claim-acknowledged-on"
                             clearable={false} disabled={{ after: new Date() }}
                             onChange={(v) => setForm({ ...form, acknowledged_on: v })} />
                </Field>
                <Field label="Acknowledgement number">
                  <Input value={form.acknowledgement_ref || ""} data-testid="claim-ack-ref"
                         onChange={(e) => setForm({ ...form, acknowledgement_ref: e.target.value })}
                         className="bg-muted/40 font-mono text-[12px]" />
                </Field>
                <Field label="Anything noted by the authority">
                  <Textarea rows={2} value={form.note || ""} data-testid="claim-ack-note"
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            className="bg-muted/40 text-[12.5px]" />
                </Field>
              </div>
            )}

            {next.key === "repayment" && (
              <div className="space-y-3">
                <div className="rounded-xl border border-border bg-muted/20 p-2.5 text-[11px] text-muted-foreground">
                  Claimed {claim.currency} {(claim.net_total ?? claim.total).toLocaleString()} ·
                  expected repayment {claim.currency} {expected?.toLocaleString()}
                  {claim.repaid_amount
                    ? ` · already received ${claim.repaid_amount.toLocaleString()}` : ""}
                </div>
                <Field label="Date received">
                  <DateField value={form.received_on} testId="claim-received-on" clearable={false}
                             disabled={{ after: new Date() }}
                             onChange={(v) => setForm({ ...form, received_on: v })} />
                </Field>
                <Field label={`Amount received (${claim.currency})`}>
                  <Input type="number" value={form.amount ?? ""} data-testid="claim-repaid-amount"
                         onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                         className="bg-muted/40 font-mono text-[12px]" />
                </Field>
                <Field label="Bank or authority reference">
                  <Input value={form.reference || ""} data-testid="claim-repaid-reference"
                         onChange={(e) => setForm({ ...form, reference: e.target.value })}
                         className="bg-muted/40 font-mono text-[12px]" />
                </Field>
                {Number(form.amount) !== expected && (
                  <Field label="Why the repayment differs"
                         hint={`${Math.abs(Number(form.amount || 0) - expected).toLocaleString()} `
                           + `${Number(form.amount || 0) > expected ? "more" : "less"} than claimed.`}>
                    <Textarea rows={2} value={form.explanation || ""}
                              data-testid="claim-variance-explanation"
                              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                              className="bg-muted/40 text-[12.5px]" />
                  </Field>
                )}
              </div>
            )}

            <DialogFooter>
              <Button disabled={busy} data-testid={`claim-${next.key}-confirm`}
                      onClick={() => post(next.key, form,
                        next.key === "submit" ? `${claim.claim_ref} marked as filed`
                          : next.key === "acknowledge" ? `${claim.claim_ref} acknowledged`
                            : `Repayment recorded against ${claim.claim_ref}`)}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {mayWrite && ["submitted", "acknowledged", "part_repaid", "repaid"].includes(claim.status)
        && live.length > 0 && (
        <Dialog open={open === "reject"} onOpenChange={(o) => {
          setOpen(o ? "reject" : "");
          setRejected([]);
          setForm(o ? { reason: "" } : {});
        }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`claim-reject-${claim.claim_ref}`}
                    className="h-7 rounded-full border-border px-2.5 text-[10px]">
              <ThumbsDown className="mr-1 h-3 w-3" /> Gifts knocked back
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Gifts the authority knocked back · {claim.claim_ref}</DialogTitle>
            </DialogHeader>
            <p className="text-[11.5px] text-muted-foreground">
              Tick the gifts that were refused. They are released from this claim and go back into
              the claimable list, so you can put them right and claim them again.
            </p>
            <div className="max-h-64 space-y-1.5 overflow-y-auto">
              {live.map((r) => (
                <label key={r.donation_id}
                       className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-border bg-muted/20 p-2.5">
                  <Checkbox checked={rejected.includes(r.donation_id)}
                            data-testid={`claim-reject-pick-${r.reference}`}
                            onCheckedChange={() => setRejected((p) => p.includes(r.donation_id)
                              ? p.filter((x) => x !== r.donation_id) : [...p, r.donation_id])} />
                  <span className="flex-1 text-[11.5px] text-foreground">
                    {r.donor_name}
                    <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                      {r.reference}
                    </span>
                  </span>
                  <span className="font-mono text-[11px] text-foreground">
                    {r.currency} {r.amount.toLocaleString()}
                  </span>
                </label>
              ))}
            </div>
            <Textarea rows={2} value={form.reason || ""} data-testid="claim-reject-reason"
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="What did the authority say?"
                      className="bg-muted/40 text-[12.5px]" />
            <DialogFooter>
              <Button disabled={busy || !rejected.length || (form.reason || "").trim().length < 10}
                      data-testid="claim-reject-confirm"
                      onClick={() => post("rejections",
                        { donation_ids: rejected, reason: form.reason },
                        `${rejected.length} gift(s) released back for a fresh claim`)}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record and release
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Button size="sm" variant="outline" onClick={() => onDownload(claim)}
              data-testid={`claim-download-${claim.claim_ref}`}
              className="h-7 rounded-full border-border px-2.5 text-[10px]">
        <Download className="mr-1 h-3 w-3" /> CSV
      </Button>
      <Button size="sm" variant="outline" onClick={() => onPrint(claim)}
              data-testid={`claim-print-${claim.claim_ref}`}
              className="h-7 rounded-full border-border px-2.5 text-[10px]">
        <Printer className="mr-1 h-3 w-3" /> Print
      </Button>

      {mayReopen && claim.status !== "reopened" && (
        <Dialog open={open === "reopen"} onOpenChange={(o) => {
          setOpen(o ? "reopen" : "");
          setForm(o ? { reason: "" } : {});
        }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`claim-reopen-${claim.claim_ref}`}
                    className="h-7 rounded-full border-border px-2.5 text-[10px]">
              <RotateCcw className="mr-1 h-3 w-3" /> Re-open
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Re-open {claim.claim_ref}</DialogTitle>
            </DialogHeader>
            <p className="text-[12px] text-muted-foreground">
              This releases all {claim.gift_count} gifts so they can be claimed again. Only you, as
              President / Chairperson, may do this and it is recorded.
            </p>
            <Textarea rows={3} value={form.reason || ""} data-testid="claim-reopen-reason"
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="Why is this claim being re-opened?"
                      className="bg-muted/40 text-[12.5px]" />
            <p className="text-[10.5px] text-muted-foreground" data-testid="claim-reopen-hint">
              {(form.reason || "").trim().length < 10
                ? `Please write at least 10 characters — ${10 - (form.reason || "").trim().length} to go.`
                : "This reason is recorded against the claim."}
            </p>
            <DialogFooter>
              <Button disabled={busy || (form.reason || "").trim().length < 10}
                      data-testid="claim-reopen-confirm"
                      onClick={() => post("reopen", { reason: form.reason },
                        `${claim.claim_ref} re-opened — the gifts are claimable again`)}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Re-open the claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
